import React, { useMemo } from 'react';
import {
  BooleanHOC,
  DefaultRenderer,
  ImageRenderer,
  MapHOC,
  PhoneHOC,
  RentalStructureHOC,
} from '../license-details-renderers';
import * as ld from './license-details-styles';
import { builder, gridRenderList } from './utils';

const factoryMap = {
  BooleanRenderer: BooleanHOC(DefaultRenderer),
  DefaultRenderer,
  ImageRenderer,
  MapRenderer: MapHOC(DefaultRenderer),
  PhoneRenderer: PhoneHOC(DefaultRenderer),
  RentalStructureRenderer: RentalStructureHOC(DefaultRenderer),
};

function List(props) {
  return (
    <ld.FlexDisplay>
      {props.list.map((item) => {
        const Component = factoryMap[item?.component] || DefaultRenderer;
        return <Component key={item.dataKey} {...item} />;
      })}
    </ld.FlexDisplay>
  );
}

function DetailsSubsection(props) {
  const { config = [], isGrid = false } = props;

  const shouldBeGrid = useMemo(
    () => gridRenderList.includes(props.type),
    [props.type]
  );

  const LabelComponent = useMemo(
    () => (isGrid ? ld.ListLabel : ld.Label),
    [isGrid]
  );

  if (!config.length) {
    if (props.displayEmptySection) {
      return (
        <ld.Flex>
          <LabelComponent>{props.label}</LabelComponent>
          <div>Information not provided</div>
        </ld.Flex>
      );
    }
    return null;
  }

  return (
    <ld.Flex>
      <LabelComponent>{props.label}</LabelComponent>

      {shouldBeGrid ? (
        <ld.RowGrid>
          {config.map((el, idx) => (
            <DetailsSubsection
              key={`nested-sub-${idx}`}
              isGrid={shouldBeGrid}
              label={`${props.listLabel} #${idx + 1}`}
              config={el}
            />
          ))}
        </ld.RowGrid>
      ) : (
        <List list={config} />
      )}
    </ld.Flex>
  );
}

function DetailsSection(props) {
  const config = useMemo(() => {
    // Each row has 2 columns (a left and a right)
    const returnConfig = props.config.map((el) => builder(el, props.data));

    return returnConfig;
  }, [props]);

  return (
    <ld.Grid columns={config.length}>
      {config
        .map((el, idx) =>
          !el?.length && !props.config[idx].displayEmptySection ? null : (
            <DetailsSubsection
              key={`details-subsection-${idx}`}
              label={props.config[idx].label}
              displayEmptySection={props.config[idx].displayEmptySection}
              listLabel={props.config[idx].listLabel}
              config={el}
              type={props.config[idx].type}
              // Used in the file display modal
              propertyAddress={props.data.propertyInfo.propertyAddress}
            />
          )
        )
        .filter(Boolean)}
    </ld.Grid>
  );
}

export function LicenseDetails({ data, config = [] }) {
  if (!config?.length) return null;

  return (
    <div>
      {config.map((el, idx) => (
        <DetailsSection
          key={`${data.licenseId}-details-section-${idx}`}
          config={el}
          data={data}
        />
      ))}
    </div>
  );
}
